import { Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  formatBucketTick as sharedFormatBucketTick,
  type IBucketAxisProps,
} from '@/lib/metric-view/bucket-format';
import { toDisplayString } from '@/lib/utils';

// Lifted from DoraMetricCard so DoraMetricCard and CodeQualityTrendCard share
// one sparkline implementation. Avoids the new-code duplication that would
// otherwise hit SonarCloud when a second card mirrors the first.

// Re-export from the shared module so existing imports
// (`from '../metric-card-sparkline'`) keep working without an additional
// migration step. New code should import from `@/lib/metric-view/bucket-format`.
export const formatBucketTick = sharedFormatBucketTick;

export interface ISparkPoint {
  bucketStart: string;
  value: number | null;
}

const STABLE_RELATIVE_VARIANCE = 0.05;

const MAX_DOT_BUCKETS = 12;

export interface ISeriesShape {
  isStable: boolean;
  nonNullCount: number;
}

export function describeSeries(series: ISparkPoint[]): ISeriesShape {
  const values = series.map((p) => p.value).filter((v): v is number => v !== null);
  if (values.length < 2) return { isStable: false, nonNullCount: values.length };

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (max === 0 && min === 0) return { isStable: true, nonNullCount: values.length };
  if (max === 0) return { isStable: false, nonNullCount: values.length };

  const spread = (max - min) / max;
  return { isStable: spread < STABLE_RELATIVE_VARIANCE, nonNullCount: values.length };
}

export function shouldShowDots(series: ISparkPoint[], shape: ISeriesShape): boolean {
  return shape.isStable && series.length <= MAX_DOT_BUCKETS;
}

export function isFullySeriesEmpty(latestValue: number | null, series: ISparkPoint[]): boolean {
  if (latestValue !== null) return false;
  return series.every((p) => p.value === null);
}

interface SparklineProps {
  series: ISparkPoint[];
  formatValue: (n: number) => string;
  unit: string;
  showDots: boolean;
  /**
   * Optional. When supplied, the X-axis uses the helper's `tickFormatter`
   * (which relabels the current week as `"5/18 – 21"`), the tooltip header
   * gains a `"(so far)"` suffix on the current bucket, and a dashed "Today"
   * `<ReferenceLine>` is drawn iff `todayLineDate !== null`.
   *
   * Callers that don't supply this prop keep the legacy `formatBucketTick`
   * behaviour with no reference line. See `bucket-format.ts`.
   */
  axis?: IBucketAxisProps;
}

/** Tooltip value formatter — combines formatValue + unit. */
export function formatTooltipValue(
  v: number | string,
  formatValue: (n: number) => string,
  unit: string,
): [string, string] {
  const n = typeof v === 'number' ? v : Number(v);
  return [unit ? `${formatValue(n)} ${unit}` : formatValue(n), ''];
}

export function formatTooltipLabel(label: unknown): string {
  return toDisplayString(label);
}

export function Sparkline({ series, formatValue, unit, showDots, axis }: Readonly<SparklineProps>) {
  return (
    <div className="mt-3 h-16 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 4, right: 4, bottom: 14, left: 4 }}>
          <XAxis
            dataKey="bucketStart"
            tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={48}
            tickFormatter={axis?.tickFormatter ?? formatBucketTick}
          />
          <YAxis hide domain={['auto', 'auto']} />
          <Tooltip
            cursor={{ stroke: 'var(--color-border)', strokeDasharray: '3 3' }}
            contentStyle={{
              fontSize: 11,
              padding: '4px 8px',
              borderRadius: 4,
              border: '1px solid var(--color-border)',
              background: 'var(--color-card)',
            }}
            formatter={(v) => formatTooltipValue(v as number | string, formatValue, unit)}
            labelFormatter={axis?.tooltipLabelFormatter ?? formatTooltipLabel}
          />
          {axis?.todayLineDate && (
            <ReferenceLine
              x={axis.todayLineDate}
              stroke="var(--color-muted-foreground)"
              strokeDasharray="4 3"
              strokeOpacity={0.7}
              ifOverflow="extendDomain"
            />
          )}
          <Line
            type="monotone"
            dataKey="value"
            stroke="var(--color-foreground)"
            strokeWidth={1.5}
            dot={showDots ? { r: 2, fill: 'var(--color-foreground)' } : false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function StabilityAnnotation({ count }: Readonly<{ count: number }>) {
  return (
    <p
      className="mt-1 text-center text-[10px] italic text-muted-foreground"
      data-testid="stability-annotation"
    >
      No change across {count} weeks
    </p>
  );
}

export function EmptyChartMessage({ message }: Readonly<{ message: string }>) {
  return (
    <div className="mt-3 flex h-16 w-full items-center justify-center rounded border border-dashed border-border/60 px-3">
      <p className="text-center text-xs text-muted-foreground">{message}</p>
    </div>
  );
}
